import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Eye, Calendar, CheckCircle2, AlertTriangle, X, Download, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import apiClient from '../../services/apiClient';

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

const ComboSuccessfulSessions = () => {
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState(daysAgo(30));
  const [toDate, setToDate] = useState(today());
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [drillIn, setDrillIn] = useState(null); // { session_id, components, ... }
  const [drillLoading, setDrillLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState('');
  const [downloadingPicked, setDownloadingPicked] = useState(false);
  const [downloadingQuickWrap, setDownloadingQuickWrap] = useState(false);

  const downloadCsvFromUrl = async (url, fallbackFilename, setBusy) => {
    setBusy(true);
    try {
      const resp = await apiClient.get(url, { responseType: 'blob' });
      const dispo = resp.headers?.['content-disposition'] || '';
      const match = dispo.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : fallbackFilename;
      const blob = new Blob([resp.data], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      const rowCount = resp.headers?.['x-row-count'];
      toast.success(rowCount
        ? `Downloaded ${filename} (${Number(rowCount).toLocaleString()} rows)`
        : `Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Download failed');
    } finally {
      setBusy(false);
    }
  };

  const downloadPickedUsns = () => {
    const params = new URLSearchParams();
    if (fromDate) params.append('from', fromDate);
    if (toDate) params.append('to', toDate);
    return downloadCsvFromUrl(
      `/combo-reconciliation/successful-sessions/picked-usns-csv?${params.toString()}`,
      `combo_v1_picked_usns_${fromDate || 'all'}_${toDate || 'all'}.csv`,
      setDownloadingPicked,
    );
  };

  const downloadQuickWrapUsns = () => {
    const params = new URLSearchParams();
    if (fromDate) params.append('from', fromDate);
    if (toDate) params.append('to', toDate);
    return downloadCsvFromUrl(
      `/combo-reconciliation/successful-sessions/quick-wrap-usns-csv?${params.toString()}`,
      `combo_floor_quick_wrap_usns_${fromDate || 'all'}_${toDate || 'all'}.csv`,
      setDownloadingQuickWrap,
    );
  };

  const exportCsv = async () => {
    setExporting(true);
    setExportProgress('Counting USNs…');
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from', fromDate);
      if (toDate) params.append('to', toDate);

      // 1) Fetch the manifest to know how many CSV files to download.
      const manifestResp = await apiClient.get(
        `/combo-reconciliation/successful-sessions/export-csv/manifest?${params.toString()}`
      );
      const { total_usns, total_files, files } = manifestResp.data;
      if (!total_usns) {
        toast.info('No consumed USNs to export in this date range');
        return;
      }
      toast.success(`Exporting ${total_usns.toLocaleString()} USN(s) across ${total_files} file(s)…`);

      // 2) Sequentially download each file part. Sequential (not parallel)
      //    so the browser doesn't crowd the server with overlapping cursors
      //    and the user sees a clean progress indicator.
      for (const f of files) {
        setExportProgress(`Downloading file ${f.file_index + 1} / ${total_files} (${f.row_count.toLocaleString()} rows)…`);
        const fileParams = new URLSearchParams(params);
        fileParams.append('file_index', String(f.file_index));
        const resp = await apiClient.get(
          `/combo-reconciliation/successful-sessions/export-csv?${fileParams.toString()}`,
          { responseType: 'blob' },
        );
        const blob = new Blob([resp.data], { type: 'text/csv' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = f.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
      }
      toast.success(`Exported ${total_files} CSV file(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Export failed');
    } finally {
      setExporting(false);
      setExportProgress('');
    }
  };

  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from', fromDate);
      if (toDate) params.append('to', toDate);
      const res = await apiClient.get(`/combo-reconciliation/successful-sessions?${params.toString()}`);
      setSessions(res.data.sessions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const drillInto = async (session_id) => {
    setDrillLoading(true);
    setDrillIn({ session_id, components: [], loading: true });
    try {
      const res = await apiClient.get(`/combo-reconciliation/successful-sessions/${session_id}/components`);
      setDrillIn(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load components');
      setDrillIn(null);
    } finally {
      setDrillLoading(false);
    }
  };

  const totalAssembled = sessions.reduce((s, x) => s + (x.assembled_quantity || 0), 0);
  const totalTarget = sessions.reduce((s, x) => s + (x.target_quantity || 0), 0);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">Successful Combo Sessions</h1>
                <p className="text-sm text-[#E8DCC8]">Completed combo assembly sessions with USN-level traceability</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
        <Card>
          <CardContent className="p-4 flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">From</label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                data-testid="from-date"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">To</label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                data-testid="to-date"
              />
            </div>
            <Button onClick={fetchSessions} disabled={isLoading} data-testid="search-btn">
              {isLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
              Apply
            </Button>
            <Button
              onClick={exportCsv}
              disabled={exporting || isLoading}
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="export-csv-btn"
              title="Export every consumed USN across the matched sessions. Files are split at 100,000 USNs each."
            >
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {exporting ? (exportProgress || 'Exporting…') : 'Export CSV'}
            </Button>
            <Button
              onClick={downloadPickedUsns}
              disabled={downloadingPicked}
              variant="outline"
              className="border-sky-300 text-sky-700 hover:bg-sky-50"
              data-testid="download-picked-usns-btn"
              title="USNs from donor bundles picked for V1 combo assembly sessions in the date range."
            >
              {downloadingPicked ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Picked USNs (V1)
            </Button>
            <Button
              onClick={downloadQuickWrapUsns}
              disabled={downloadingQuickWrap}
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="download-quick-wrap-usns-btn"
              title="USNs that ended up in Combo Floor Quick-Wrap bundles in the date range."
            >
              {downloadingQuickWrap ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Quick-Wrap USNs
            </Button>
            <div className="ml-auto flex gap-3">
              <Badge variant="outline" className="text-base px-3 py-1">
                Sessions: <b className="ml-1">{sessions.length}</b>
              </Badge>
              <Badge variant="outline" className="text-base px-3 py-1">
                Combos Assembled: <b className="ml-1 text-green-700">{totalAssembled}</b> / {totalTarget}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Flow</TableHead>
                  <TableHead>Session ID</TableHead>
                  <TableHead>Combo</TableHead>
                  <TableHead>Design</TableHead>
                  <TableHead>Shade</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead className="text-right">Target</TableHead>
                  <TableHead className="text-right">Assembled</TableHead>
                  <TableHead className="text-right">Components</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="text-center text-gray-500 py-8">No sessions in this date range.</TableCell></TableRow>
                ) : sessions.map(s => (
                  <TableRow key={s.session_id} data-testid={`session-row-${s.session_id}`}>
                    <TableCell>
                      <Badge variant={s.flow === 'V2' ? 'default' : 'secondary'}>{s.flow}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{s.session_id}</TableCell>
                    <TableCell className="font-mono">{s.combo_barcode}</TableCell>
                    <TableCell>{s.design_name}</TableCell>
                    <TableCell>{s.shade}</TableCell>
                    <TableCell>{s.size}</TableCell>
                    <TableCell className="text-right">{s.target_quantity}</TableCell>
                    <TableCell className="text-right font-semibold text-green-700">{s.assembled_quantity}</TableCell>
                    <TableCell className="text-right">{s.components_count}</TableCell>
                    <TableCell className="text-xs text-gray-600">
                      {s.completed_at ? new Date(s.completed_at).toLocaleString() : (s.updated_at ? new Date(s.updated_at).toLocaleString() : '—')}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => drillInto(s.session_id)} data-testid={`drill-${s.session_id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/wms/admin/combo-variance/${s.session_id}`)}
                          data-testid={`variance-${s.session_id}`}
                        >
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>

      {drillIn && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setDrillIn(null)}>
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Component USN Consumption</h3>
                <p className="text-xs text-gray-500 font-mono">{drillIn.session_id} ({drillIn.flow}) — {drillIn.combo_barcode} • Target {drillIn.target_quantity} • Assembled {drillIn.assembled_quantity}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setDrillIn(null)} data-testid="close-drill">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 space-y-4">
              {drillLoading && <div className="flex items-center justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>}
              {(drillIn.components || []).map(c => (
                <Card key={c.component_barcode} data-testid={`comp-${c.component_barcode}`}>
                  <CardHeader className="py-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-sm font-mono">{c.component_barcode}</CardTitle>
                        <p className="text-xs text-gray-600">{c.component_name} • {c.category}</p>
                      </div>
                      <div className="flex gap-3">
                        <Badge variant="outline">Demand: <b className="ml-1">{c.demand}</b></Badge>
                        <Badge variant="outline" className="text-green-700">Consumed: <b className="ml-1">{c.consumed_count}</b></Badge>
                        {c.variance !== 0 && (
                          <Badge variant="outline" className={c.variance > 0 ? 'text-red-700' : 'text-amber-700'}>
                            Variance: <b className="ml-1">{c.variance > 0 ? '-' : '+'}{Math.abs(c.variance)}</b>
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xs font-mono text-gray-700 max-h-48 overflow-y-auto">
                      {(c.consumed_usn_codes || []).length === 0
                        ? <span className="text-gray-400">No USN-level records found.</span>
                        : c.consumed_usn_codes.join(', ')}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComboSuccessfulSessions;
